import i18n from '@/utils/i18n';
import React from 'react';
import {
  Avatar,
  createRestaurantAvatarProps,
  Typography,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { formatMonthDayInTimezone, formatTimeslot } from '@/utils/date';
import { GetAvailableOrderFulfillmentResponse } from '@/types';
import { getFulfillmentDisplayName } from '@/experiences/sales-portal/blocs/utils/fulfillment-utils';
import { OrderEntryMobileHeaderChevron } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-header-chevron';
import { Pressable } from 'react-native';
import { SavedSelectedFulfillment } from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';
import { TimeZoneMap } from '@domain/fulfillment-settings/types/types';

// Formats fulfillment setting into "Deliver Jun 12"
export const formatFulfillmentSummary = (
  savedSelectedFulfillment: SavedSelectedFulfillment,
  availableFulfillments: GetAvailableOrderFulfillmentResponse[],
  timezones: TimeZoneMap,
): string => {
  const displayName = getFulfillmentDisplayName(
    savedSelectedFulfillment.type,
    availableFulfillments,
  );
  const date = formatMonthDayInTimezone(
    savedSelectedFulfillment.date,
    timezones.restaurant,
  );
  const timeslot = savedSelectedFulfillment.timeslot
    ? formatTimeslot(savedSelectedFulfillment.timeslot, timezones, true)
    : '';

  return [displayName, date, timeslot].filter(Boolean).join(' ');
};

interface OrderEntryMobileHeaderProps {
  availableFulfillments: GetAvailableOrderFulfillmentResponse[];
  isExpanded: boolean;
  onPress: () => void;
  orderGuideName: string;
  restaurantName: string;
  restaurantUUID: string;
  savedSelectedFulfillment: SavedSelectedFulfillment;
  timezones: TimeZoneMap;
}

export const OrderEntryMobileHeader: React.FC<OrderEntryMobileHeaderProps> = ({
  availableFulfillments,
  isExpanded,
  onPress,
  orderGuideName,
  restaurantName,
  restaurantUUID,
  savedSelectedFulfillment,
  timezones,
}) => {
  const fulfillmentSummary = formatFulfillmentSummary(
    savedSelectedFulfillment,
    availableFulfillments,
    timezones,
  );

  return (
    <Pressable
      accessibilityLabel={i18n.t(
        'sales_portal.order_entry.mobile_header.a11y.expand',
      )}
      accessibilityRole="button"
      accessibilityState={{ expanded: isExpanded }}
      onPress={onPress}
    >
      <XStack
        alignItems="center"
        backgroundColor="$bgPrimaryDefault"
        borderBottomColor="$borderPrimary"
        borderBottomLeftRadius="$18"
        borderBottomRightRadius="$18"
        borderBottomWidth={1}
        borderLeftColor="$borderPrimary"
        borderLeftWidth={1}
        borderRightColor="$borderPrimary"
        borderRightWidth={1}
        gap="$12"
        paddingBottom="$16"
        paddingHorizontal="$16"
        paddingTop="$8"
        width="100%"
      >
        <Avatar
          {...createRestaurantAvatarProps(restaurantName, restaurantUUID)}
          name={restaurantName}
          size="$md"
        />
        <YStack flex={1} gap="$2" minWidth={0}>
          <Typography numberOfLines={1} textAlign="left" variant="label">
            {restaurantName}
          </Typography>
          <Typography
            color="$textSecondary"
            numberOfLines={1}
            size="$sm"
            textAlign="left"
          >
            {`${fulfillmentSummary} · ${orderGuideName}`}
          </Typography>
        </YStack>
        <OrderEntryMobileHeaderChevron isExpanded={isExpanded} />
      </XStack>
    </Pressable>
  );
};
